<template>
  <div class="line-chat-messages">
    <div v-if="showDate" class="chat-date">今天</div>

    <!-- 歡迎訊息 -->
    <div v-if="welcomeText" class="msg-row msg-left">
      <div class="bot-avatar"><i class="ri-store-2-fill"></i></div>
      <div class="bubble-text">{{ welcomeText }}</div>
    </div>

    <!-- 無內容提示 -->
    <div v-if="!hasContent && emptyText" class="empty-preview">
      <i class="ri-chat-smile-2-line"></i>
      <span>{{ emptyText }}</span>
    </div>

    <template v-for="(msg, msgIdx) in messages" :key="msgIdx">

      <!-- ── 使用者訊息（右側綠色氣泡） ───────────────────── -->
      <div v-if="msg.role === 'user'" class="msg-row msg-right">
        <div class="bubble-text bubble-user">{{ msg.text }}</div>
      </div>

      <!-- ── 系統提示（居中灰色） ──────────────────────────── -->
      <div v-else-if="msg.role === 'tip'" class="sim-tip">
        <i class="ri-information-line"></i>
        <span>{{ msg.text }}</span>
      </div>

      <!-- ── Bot 文字（含 LINE emoji） ────────────────────── -->
      <div v-else-if="msgType(msg) === 'text'" class="msg-row msg-left">
        <div class="bot-avatar"><i class="ri-store-2-fill"></i></div>
        <div class="bubble-text line-text-with-emoji">
          <template v-if="!msg.textContent && !(msg.textEmojis && msg.textEmojis.length)">
            (空白訊息)
          </template>
          <template v-else>
            <template
              v-for="(seg, si) in lineTextPreviewSegments(msg.textContent, msg.textEmojis)"
              :key="si"
            >
              <span v-if="seg.type === 'text'" class="line-text-run">{{ seg.text }}</span>
              <img
                v-else
                class="line-emoji-inline"
                :src="emojiStickerUrl(seg.productId, seg.emojiId)"
                width="22"
                height="22"
                alt=""
                loading="lazy"
                decoding="async"
                @error="handleImgError"
              />
            </template>
          </template>
        </div>
      </div>

      <!-- ── Bot 圖片 ──────────────────────────────────────── -->
      <div v-else-if="msgType(msg) === 'image'" class="msg-row msg-left">
        <div class="bot-avatar"><i class="ri-store-2-fill"></i></div>
        <div class="bubble-image">
          <img v-if="msg.imageUrl" :src="msg.imageUrl" alt="" @error="handleImgError" />
          <div v-else class="image-placeholder">
            <i class="ri-image-line"></i>
            <span>尚未上傳圖片</span>
          </div>
        </div>
      </div>

      <!-- ── Bot 影片（縮圖 + 播放鍵）───────────────────────── -->
      <div v-else-if="msgType(msg) === 'video'" class="msg-row msg-left">
        <div class="bot-avatar"><i class="ri-store-2-fill"></i></div>
        <div class="bubble-video">
          <img v-if="msg.previewImageUrl" :src="msg.previewImageUrl" alt="" @error="handleImgError" />
          <div v-else class="video-placeholder">
            <i class="ri-video-line"></i>
          </div>
          <div class="play-overlay">
            <div class="play-btn"><i class="ri-play-fill"></i></div>
          </div>
        </div>
      </div>

      <!-- ── Bot 圖片輪播 ───────────────────────────────────── -->
      <template v-else-if="msgType(msg) === 'image_carousel'">
        <div class="msg-row msg-left" style="margin-bottom: 0;">
          <div class="bot-avatar"><i class="ri-store-2-fill"></i></div>
        </div>
        <p
          v-if="msg.carouselItems && msg.carouselItems.length > 1"
          class="img-carousel-hint"
        >
          <i class="ri-arrow-left-right-line" aria-hidden="true"></i>
          左右滑動或拖曳可查看更多
        </p>
        <div class="img-carousel-scroll" @mousedown="startDragOn($event)">
          <div class="img-carousel-track">
            <div
              v-for="(item, ci) in msg.carouselItems"
              :key="ci"
              class="carousel-card"
            >
              <img
                v-if="item.imageUrl"
                :src="item.imageUrl"
                alt=""
                draggable="false"
                @error="handleImgError"
                @dragstart.prevent
              />
              <div v-else class="image-placeholder small">
                <i class="ri-image-line"></i>
              </div>
              <div v-if="item.label" class="carousel-label">{{ item.label }}</div>
            </div>
            <div v-if="!msg.carouselItems || !msg.carouselItems.length" class="image-placeholder">
              <i class="ri-layout-grid-line"></i>
              <span>尚未新增圖片</span>
            </div>
          </div>
        </div>
      </template>

      <!-- ── Bot Imagemap ──────────────────────────────────── -->
      <div v-else-if="msgType(msg) === 'imagemap'" class="msg-row msg-left">
        <div class="bot-avatar"><i class="ri-store-2-fill"></i></div>
        <div
          class="bubble-imagemap"
          @mouseenter="msg._showAreas = true"
          @mouseleave="msg._showAreas = false"
        >
          <img v-if="msg.imagemapUrl" :src="msg.imagemapUrl" alt="" @error="handleImgError" />
          <div v-else class="image-placeholder">
            <i class="ri-collage-line"></i>
            <span>尚未上傳圖文底圖</span>
          </div>
          <template v-if="msg.imagemapUrl && msg._showAreas && msg.imagemapAreas && msg.imagemapAreas.length">
            <div
              v-for="(a, ai) in msg.imagemapAreas"
              :key="ai"
              class="imap-area"
              :style="{ left: (a.x * 100) + '%', top: (a.y * 100) + '%', width: (a.w * 100) + '%', height: (a.h * 100) + '%' }"
            >
              <span class="imap-num">{{ ai + 1 }}</span>
            </div>
          </template>
        </div>
      </div>

      <!-- ── Bot Flex / Flex Carousel ──────────────────────── -->
      <div
        v-else-if="msgType(msg) === 'flex' || msgType(msg) === 'flex_carousel'"
        class="msg-row msg-left msg-full"
      >
        <div class="bot-avatar"><i class="ri-store-2-fill"></i></div>
        <div class="flex-wrap">
          <FlexRenderer v-if="msg.flexContent" :content="msg.flexContent" />
          <div v-else class="empty-flex">
            <i class="ri-layout-3-line"></i>
            <span>尚未設定 Flex 內容</span>
          </div>
        </div>
      </div>

      <!-- ── Bot 未知類型 fallback ─────────────────────────── -->
      <div v-else-if="msg.role !== 'user' && msg.role !== 'tip'" class="msg-row msg-left">
        <div class="bot-avatar"><i class="ri-store-2-fill"></i></div>
        <div class="bubble-media">
          <i class="ri-message-3-line"></i>
          <span>{{ msgType(msg) || '未知' }} 訊息</span>
        </div>
      </div>

    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import FlexRenderer from '@/views/client/line-msg-templates/components/FlexRenderer.vue';
import { isLineEmojiPlaceholderChar } from '@/utils/lineEmojiPlaceholder.js';

const props = defineProps({
  messages:    { type: Array,  default: () => [] },
  showDate:    { type: Boolean, default: true },
  welcomeText: { type: String, default: '' },
  emptyText:   { type: String, default: '' }
});

const hasContent = computed(() => props.messages.length > 0);

/** role 不存在時預設為 bot */
function msgType(msg) {
  return msg.messageType || '';
}

function emojiStickerUrl(productId, emojiId) {
  const id = String(emojiId).padStart(3, '0');
  return `https://stickershop.line-scdn.net/sticonshop/v1/sticon/${productId}/android/${id}.png`;
}

/**
 * 將 text 中的 $ 依 emojis[].index 替換為預覽用片段（與 LINE UTF-16 index 對齊）
 */
function lineTextPreviewSegments(text, emojis) {
  const t = text ?? '';
  const list = (emojis || [])
    .filter(
      (e) =>
        typeof e?.index === 'number' &&
        Number.isFinite(e.index) &&
        e?.productId &&
        e?.emojiId != null &&
        String(e.emojiId).trim() !== ''
    )
    .map((e) => ({
      index: e.index,
      productId: String(e.productId).trim(),
      emojiId: String(e.emojiId).padStart(3, '0')
    }))
    .sort((a, b) => a.index - b.index);

  if (!list.length) return [{ type: 'text', text: t }];

  const out = [];
  let cursor = 0;
  for (const em of list) {
    const idx = em.index;
    if (idx < cursor) continue;
    if (idx > t.length) break;
    if (idx > cursor) out.push({ type: 'text', text: t.slice(cursor, idx) });
    const ch = t.charAt(idx);
    if (isLineEmojiPlaceholderChar(ch)) {
      out.push({ type: 'emoji', productId: em.productId, emojiId: em.emojiId });
      cursor = idx + 1;
    } else {
      out.push({ type: 'text', text: t.slice(cursor, idx + 1) });
      cursor = idx + 1;
    }
  }
  if (cursor < t.length) out.push({ type: 'text', text: t.slice(cursor) });
  return out.length ? out : [{ type: 'text', text: t }];
}

function handleImgError(e) {
  e.target.style.display = 'none';
}

function startDragOn(e) {
  e.preventDefault();
  const el = e.currentTarget;
  if (!el) return;
  let isDragging = true;
  const dragStartX = e.pageX - el.offsetLeft;
  const dragScrollLeft = el.scrollLeft;
  el.style.cursor = 'grabbing';
  const onMove = (ev) => {
    if (!isDragging) return;
    ev.preventDefault();
    const x = ev.pageX - el.offsetLeft;
    el.scrollLeft = dragScrollLeft - (x - dragStartX);
  };
  const onUp = () => {
    isDragging = false;
    el.style.cursor = 'grab';
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseup', onUp);
  };
  document.addEventListener('mousemove', onMove);
  document.addEventListener('mouseup', onUp);
}
</script>

<style scoped>
.line-chat-messages {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.chat-date {
  text-align: center;
  font-size: 11px;
  color: #8b8b8b;
  background: rgba(0,0,0,.06);
  border-radius: 12px;
  padding: 2px 12px;
  align-self: center;
  margin-bottom: 4px;
}

.empty-preview {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  gap: 8px;
  font-size: 13px;
  text-align: center;
  padding: 24px 16px;
}
.empty-preview i { font-size: 36px; }

/* ── 訊息列 ─────────────────────────────────────────────── */
.msg-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.msg-left  { justify-content: flex-start; }
.msg-right { justify-content: flex-end; }
.msg-full  { width: 100%; overflow: hidden; min-width: 0; }

/* ── 使用者氣泡（右側綠色） ─────────────────────────────── */
.bubble-user {
  background: #06C755;
  color: #fff;
  border-radius: 18px 18px 4px 18px;
}

/* ── 系統提示（居中灰色） ───────────────────────────────── */
.sim-tip {
  display: flex;
  align-items: center;
  gap: 5px;
  align-self: center;
  font-size: 10px;
  color: #6b7280;
  background: rgba(0,0,0,.06);
  border-radius: 10px;
  padding: 3px 10px;
}
.sim-tip i { font-size: 11px; color: #3b82f6; }

/* ── Bot 頭像 ───────────────────────────────────────────── */
.bot-avatar {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #06C755;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 16px;
}

/* ── 文字氣泡 ───────────────────────────────────────────── */
.bubble-text {
  background: #fff;
  color: #1f2937;
  padding: 10px 14px;
  border-radius: 18px 18px 18px 4px;
  max-width: 200px;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  box-shadow: 0 1px 2px rgba(0,0,0,.06);
}
.line-text-with-emoji { min-height: 1.2em; }
.line-text-run { vertical-align: middle; }
.line-emoji-inline {
  width: 22px;
  height: 22px;
  vertical-align: text-bottom;
  display: inline-block;
  object-fit: contain;
  margin: 0 1px;
}

/* ── 一般媒體 placeholder（icon + label）──────────────── */
.bubble-media {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  color: #374151;
  padding: 8px 14px;
  border-radius: 18px 18px 18px 4px;
  font-size: 11px;
  box-shadow: 0 1px 2px rgba(0,0,0,.06);
}
.bubble-media i { font-size: 14px; color: #6b7280; }

/* ── 圖片 ───────────────────────────────────────────────── */
.bubble-image {
  max-width: 190px;
  border-radius: 13px;
  overflow: hidden;
}
.bubble-image img { width: 100%; display: block; }

/* ── 影片 ───────────────────────────────────────────────── */
.bubble-video {
  max-width: 190px;
  border-radius: 13px;
  overflow: hidden;
  position: relative;
}
.bubble-video img { width: 100%; display: block; }
.video-placeholder {
  width: 190px;
  height: 120px;
  background: #374151;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 32px;
}
.play-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.play-btn {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(0,0,0,.5);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 22px;
}

/* ── 圖片輪播 ───────────────────────────────────────────── */
.img-carousel-hint {
  margin: 0 0 6px;
  padding: 0 12px 0 42px;
  font-size: 11px;
  line-height: 1.35;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 5px;
}
.img-carousel-hint i { flex-shrink: 0; font-size: 14px; color: #9ca3af; }
.img-carousel-scroll {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  cursor: grab;
  padding-bottom: 4px;
}
.img-carousel-scroll::-webkit-scrollbar { height: 4px; }
.img-carousel-scroll::-webkit-scrollbar-track { background: transparent; }
.img-carousel-scroll::-webkit-scrollbar-thumb { background: transparent; border-radius: 4px; }
.img-carousel-scroll:hover::-webkit-scrollbar-thumb,
.img-carousel-scroll:active::-webkit-scrollbar-thumb { background: rgba(0,0,0,.2); }
.img-carousel-track {
  display: inline-flex;
  gap: 8px;
  padding: 2px 2px 2px 42px;
}
.carousel-card {
  flex: 0 0 130px;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0,0,0,.1);
}
.carousel-card img { width: 100%; height: 95px; object-fit: cover; display: block; }
.carousel-label {
  padding: 6px 8px;
  font-size: 12px;
  color: #3B82F6;
  text-align: center;
  font-weight: 500;
}

/* ── Imagemap ───────────────────────────────────────────── */
.bubble-imagemap {
  max-width: 220px;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
}
.bubble-imagemap img { width: 100%; display: block; }
.imap-area {
  position: absolute;
  background: rgba(59, 130, 246, .2);
  border: 1px dashed rgba(59, 130, 246, .6);
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}
.imap-num {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #3B82F6;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0,0,0,.2);
}

/* ── Flex ───────────────────────────────────────────────── */
.flex-wrap { flex: 1; min-width: 0; overflow: hidden; }
.empty-flex {
  width: 100%;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  color: #9ca3af;
  font-size: 12px;
  background: #f9fafb;
  border-radius: 14px;
}
.empty-flex i { font-size: 28px; }

/* ── 共用 image placeholder ─────────────────────────────── */
.image-placeholder {
  width: 190px;
  height: 120px;
  background: #f3f4f6;
  border-radius: 13px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  gap: 4px;
  font-size: 12px;
}
.image-placeholder i { font-size: 28px; }
.image-placeholder.small { width: 130px; height: 95px; }
</style>
